import { useState, useCallback } from "react";
import { graphqlClient } from "@/services/core/graphqlClient";

export interface FacilityAvailabilityAuditLog {
  facility_availability_id: number;
  action_type: string;
  action_by: number;
  action_datetime: string;
  availability_old: any;
  availability_new: any;
  day_time_changes: any;
  api_metadata: any;
}

interface FacilityAvailabilityAuditLogResponse {
  ref_facility_availability_audit_log: FacilityAvailabilityAuditLog[];
}

const GET_FACILITY_AVAILABILITY_AUDIT_LOG = `
  query GetFacilityAvailabilityAuditLog($facilityAvailabilityId: Int!) {
    ref_facility_availability_audit_log(
      where: { facility_availability_id: { _eq: $facilityAvailabilityId } }
      order_by: { action_datetime: desc }
    ) {
      facility_availability_id
      action_type
      action_by
      action_datetime
      availability_old
      availability_new
      day_time_changes
      api_metadata
    }
  }
`;

export const useFacilityAvailabilityAuditLog = () => {
  const [auditLogs, setAuditLogs] = useState<FacilityAvailabilityAuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAuditLogs = useCallback(async (facilityAvailabilityId: number) => {
    setLoading(true);
    setError(null);

    try {
      const hasuraToken = localStorage.getItem("hasuraToken") || "";

      const response = await graphqlClient.query<FacilityAvailabilityAuditLogResponse>(
        GET_FACILITY_AVAILABILITY_AUDIT_LOG,
        { facilityAvailabilityId },
        hasuraToken,
        "GetFacilityAvailabilityAuditLog"
      );

      if (response.success && response.data) {
        setAuditLogs(response.data.ref_facility_availability_audit_log);
      } else {
        setError("Failed to fetch audit logs");
        setAuditLogs([]);
      }
    } catch (err: any) {
      console.error("Error fetching facility availability audit logs:", err);
      setError(err.message || "An error occurred");
      setAuditLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearAuditLogs = useCallback(() => {
    setAuditLogs([]);
    setError(null);
  }, []);

  return {
    auditLogs,
    loading,
    error,
    fetchAuditLogs,
    clearAuditLogs,
  };
};
